import { useState, useEffect } from "react";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import API from "api/endpoints";
import axiosInstance from "api/axiosInstance";
import { JobApplicationStatusEnum } from "Constants/Enums/JobApplicantStatusEnum";

export default function JobOfferService(jobPostingId) {
  const [jobOfferOpen, setJobOfferOpen] = useState(false);
  const [loadingJobOffer, setLoadingEmails] = useState({});
  const [jobOfferEmployees, setJobOfferEmployees] = useState([]);

  const [formData, setFormData] = useState({
    ndafile: null,
    ncafile: null,
    employeeAgreement: null,
    i_nine: null,
    w_four: null,
    offerLetter: null,
    status: "Send",
  });

  const [jobOfferEmployeeId, setJobOfferEmployeeId] = useState(null);
  const [jobOfferemail, setJobOfferemail] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [jobOfferScheduled, setJobOfferScheduled] = useState({});
  const [jobOffer, setJobOffer] = useState({});

  useEffect(() => {
    if (!jobPostingId || isNaN(Number(jobPostingId))) return;
    fetchEmployeesByjobPostingId(jobPostingId);
  }, [jobPostingId]);

  const fetchEmployeesByjobPostingId = async (jobPostingId) => {
    if (!jobPostingId || isNaN(Number(jobPostingId))) {
      console.warn("JOB OFFER SERVICE: Skipping API call. Invalid jobPostingId:", jobPostingId);
      return;
    }

    try {
      const response = await axiosInstance.get(
        API.hiringJobApplicant.getListByPosting(jobPostingId)
      );

      // Process the response and update state
      const employees = response.data.applicants || [];
      setJobOfferEmployees(employees);

      const scheduledData = employees.map((info) => ({
        key: info.jobApplicantId.toString(),
        employeeId: info.jobApplicantId,
        status: info.status,
        emailSent: info.emailSent,
        canSendOffer: isJobOfferAllowed(info.status),
      }));
      setJobOfferScheduled(scheduledData);
    } catch (error) {
      console.error("JOB OFFER SERVICE: Error fetching data:", error);
    }
  };

  const isJobOfferAllowed = (status) => {
    return (
      status === JobApplicationStatusEnum.Interview ||
      status === JobApplicationStatusEnum.FeedbackAdded ||
      status === JobApplicationStatusEnum.DocumentSend
    );
  };

  const handleContractChange = (e, fileType) => {
    const file = e.target.files[0];
    const allowedExtensions = [".pdf", ".PDF", ".docx", ".DOCX", ".doc", ".DOC"];

    if (file) {
      const fileExtension = file.name.split(".").pop();
      if (!allowedExtensions.includes("." + fileExtension)) {
        setErrorMessage("Unsupported file type. Only PDF and DOCX files are allowed.");
        setFormData({ ...formData, [fileType]: null });
        return;
      }
      setErrorMessage("");
      setFormData({ ...formData, [fileType]: file });
    }
  };

  const showjobOfferModal = (employeeId, email) => {
    setJobOfferEmployeeId(employeeId);
    setJobOfferemail(email);
    setJobOfferOpen(true);
  };

  const handleJobOfferCancel = () => {
    setJobOfferOpen(false);
  };

  const handleJobOfferClick = () => {
    setJobOfferOpen(false);
    JobOfferClick();
  };

  const JobOfferClick = async () => {
    const formDataToSend = new FormData();

    // Check if files are present before appending
    if (formData.ndafile) formDataToSend.append("Ndafile", formData.ndafile);
    if (formData.ncafile) formDataToSend.append("Ncafile", formData.ncafile);
    if (formData.employeeAgreement)
      formDataToSend.append("EmployeeAgreement", formData.employeeAgreement);
    if (formData.i_nine) formDataToSend.append("I_nine", formData.i_nine);
    if (formData.w_four) formDataToSend.append("W_four", formData.w_four);
    if (formData.offerLetter) formDataToSend.append("OfferLetter", formData.offerLetter);

    // Append other data
    formDataToSend.append("JobApplicantId", jobOfferEmployeeId);

    const requiredFiles = [
      { key: "ndafile", name: "NDA" },
      { key: "ncafile", name: "NCA" },
      { key: "employeeAgreement", name: "Employee Agreement" },
      { key: "i_nine", name: "I-9" },
      { key: "w_four", name: "W-4" },
      { key: "offerLetter", name: "Offer Letter" },
    ];

    const missingFiles = requiredFiles.filter((file) => !formData[file.key]);

    if (missingFiles.length > 0) {
      const missingFileNames = missingFiles.map((file) => file.name).join(", ");
      toast.error(`Please upload all required files: ${missingFileNames}`);
      return;
    }

    // Validate jobApplicantId
    if (!jobOfferEmployeeId) {
      toast.error("Employee ID is missing. Please try again.");
      return;
    }

    try {
      setLoadingEmails((prev) => ({
        ...prev,
        [jobOfferEmployeeId]: true,
      }));
      const response = await axiosInstance.post(API.hiringContracts.sendContracts, formDataToSend, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      if (response.status === 200) {
        // Reset form after successful submission
        setJobOfferOpen(false);
        setErrorMessage("");
        setJobOffer((prevState) => ({
          ...prevState,
          [jobOfferEmployeeId]: true,
        }));
        toast.success("Contract sent successfully!");

        fetchEmployeesByjobPostingId(jobPostingId); // Refresh the employee list
      } else {
        toast.error("Failed to submit the contract.");
      }
    } catch (error) {
      console.error("JOB OFFER SERVICE: Error:", error);
      toast.error("Submission failed.");
    } finally {
      setLoadingEmails((prev) => ({
        ...prev,
        [jobOfferEmployeeId]: false,
      }));
    }
  };

  return {
    jobOfferOpen,
    formData,
    jobOffer,
    setFormData,
    handleContractChange,
    showjobOfferModal,
    handleJobOfferCancel,
    handleJobOfferClick,
    jobOfferScheduled,
    loadingJobOffer,
    jobOfferEmployees,
  };
}
